import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import {
  EspSummary,
  ToaOutput,
  ToaSummary,
  transformAccountProcessing,
  transformEsp,
  transformOnlineAccountants,
} from '../../api/batch11Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

export type ClientKind = 'toa' | 'esp' | 'fourfifty';

interface PanelProps {
  mission: Mission;
  client: ClientKind;
  onBack: () => void;
}

const NAMES: Record<ClientKind, string> = { toa: 'The Online Accountants', esp: 'ESP', fourfifty: 'FourFifty' };
const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

function ClientTransformerPanel({ mission, client, onBack }: PanelProps) {
  const name = NAMES[client];
  const [files, setFiles] = useState<File[]>([]);
  const [output, setOutput] = useState<ToaOutput>('with_metadata');
  const { status, error, result, fail, run } = useFileRun<FileResult<ToaSummary | EspSummary>>();

  const steps = [
    `Upload the ${name} input workbook (.xlsx${client === 'fourfifty' ? ' or .xlsm' : ''}).`,
    client === 'toa'
      ? 'Choose whether to download the version with metadata (recommended), without, or both as a ZIP.'
      : `Click Run ${name} Transformation.`,
    'The transformed workbook downloads automatically.',
  ];

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload the input workbook first.');
      return;
    }
    await run(
      () => (client === 'toa'
        ? transformOnlineAccountants(files[0], output)
        : client === 'fourfifty'
          ? transformAccountProcessing(files[0])
          : transformEsp(files[0])),
      'Transformation failed.',
    );
  };

  const toa = client === 'toa' ? (result?.summary as ToaSummary | null | undefined) : null;
  const log = result?.summary?.log;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={steps}>
      <FileDrop
        label={`${name} input workbook`}
        required
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      {client === 'toa' && (
        <div className="mb-6 rounded-lg border border-border bg-muted p-4">
          <Label htmlFor="ct-output" className="mb-1.5">
            Output
          </Label>
          <select id="ct-output" className={selectClass} value={output} onChange={(e) => setOutput(e.target.value as ToaOutput)}>
            <option value="with_metadata">With metadata (recommended)</option>
            <option value="without_metadata">Without metadata</option>
            <option value="both">Both (ZIP)</option>
          </select>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Transforming…' : `Run ${name} Transformation`}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Transforming…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          {toa && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ['Total sheets', toa.total_sheets],
                  ['Transformed sheets', toa.transformed_sheets],
                  ['Status', 'Complete'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                    <div className="text-lg font-bold text-foreground">{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
              <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
                <summary className="cursor-pointer text-foreground">All sheet names</summary>
                <ol className="mt-2 list-decimal pl-5 text-xs">
                  {toa.sheet_names.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ol>
              </details>
            </>
          )}
          {log && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">Execution log</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload an Excel file to run the {name} transformation.</InfoNotice>}
    </MissionShell>
  );
}

export default ClientTransformerPanel;
